import { DateTime } from 'luxon';
import {
  type KeyboardEvent,
  type ChangeEvent,
  createRef,
  useEffect,
  useState,
} from 'react';
import type ReactDatePicker from 'react-datepicker';
import { DatePicker } from '@/pages/Schedule/components';
import { Checkbox, Flex, Input, Text, forwardRef } from '@/ui';
import { maybeAdjustDefaultYear } from '@/utils/dates';

type Props = {
  hasRecurrence: boolean;
  isAllDay: boolean;
  /** Share the row's width rather than taking 125px each, for narrow containers. */
  isFluid?: boolean;
  startDate: DateTime;
  endDate: DateTime;
  untilDate: DateTime | null;
  fallbackStartDate?: DateTime;
  fallbackEndDate?: DateTime;
  startPlaceholder?: string;
  endPlaceholder?: string;
  showStartPlaceholder?: boolean;
  showEndPlaceholder?: boolean;
  onStartChange: (startDate: DateTime) => void;
  onEndChange: (endDate: DateTime) => void;
  onUntilChange: (untilDate: DateTime | null) => void;
};

const CalendarInput = forwardRef((props, ref) => (
  <Input
    _hover={{ borderColor: 'gray.300' }}
    borderColor="gray.100"
    borderRadius="md"
    ref={ref}
    size="sm"
    textAlign="center"
    variant="primary"
    w="125px"
    {...props}
  />
));

export const DateRangeInput = ({
  hasRecurrence,
  isAllDay,
  isFluid,
  startDate,
  endDate,
  untilDate,
  startPlaceholder,
  endPlaceholder,
  showStartPlaceholder,
  showEndPlaceholder,
  fallbackStartDate,
  fallbackEndDate,
  onStartChange,
  onEndChange,
  onUntilChange,
}: Props) => {
  // The picker's own wrapper sits between the row and the input and is
  // inline-block, so it would otherwise hold the input at its intrinsic width.
  const fluidSx = {
    '> div': { flex: 1, minWidth: 0 },
    '.react-datepicker-wrapper': { display: 'block' },
  };
  const inputWidth = isFluid ? 'full' : '125px';

  const startDatePickerRef = createRef<ReactDatePicker>();
  const endDatePickerRef = createRef<ReactDatePicker>();
  const [isMouseOverCalendar, setIsMouseOverCalendar] = useState(false);
  const adjustedEndDate = endDate.minus({ days: isAllDay ? 1 : 0 });

  const [indefinitely, setIndefinitely] = useState(hasRecurrence && !untilDate);
  useEffect(() => {
    setIndefinitely(hasRecurrence && !untilDate);
  }, [hasRecurrence, untilDate]);

  // The selected end date is either the endDate (non-recurring) or untilDate
  // (recurring with indefinitely deselected) or undefined (recurring with
  // indefinitely selected).
  let selectedEndDate: DateTime | null = null;
  if (hasRecurrence && !indefinitely) {
    selectedEndDate = untilDate;
  } else if (!hasRecurrence) {
    selectedEndDate = adjustedEndDate;
  }

  const handleStartChange = (date: Date | null) => {
    if (!date) return;
    const newStartDate = maybeAdjustDefaultYear(DateTime.fromJSDate(date));
    onStartChange(newStartDate);
  };

  const handleEndChange = (date: Date | null) => {
    if (!date) return;
    const newEndDate = maybeAdjustDefaultYear(
      DateTime.fromJSDate(date).plus({
        days: isAllDay ? 1 : 0,
      })
    );
    onEndChange(newEndDate);
  };

  const handleUntilChange = (date: Date | null) => {
    setIndefinitely(false);
    onUntilChange(date ? DateTime.fromJSDate(date).endOf('day') : null);
  };

  const handleIndefinitelyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    setIndefinitely(checked);
    if (checked) {
      onUntilChange(null);
      endDatePickerRef.current?.setOpen(false);
    }
  };

  // Move focus to the next input on tab instead of entering the calendar
  //github.com/Hacker0x01/react-datepicker/issues/2058#issuecomment-589451843
  const handleStartKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      startDatePickerRef.current?.setOpen(false);
    }
  };
  const handleEndKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      endDatePickerRef.current?.setOpen(false);
    }
  };

  return (
    <Flex direction="column" gap="1">
      <Flex align="center" gap="2" sx={isFluid ? fluidSx : undefined}>
        <DatePicker
          allowSameDay={showStartPlaceholder}
          dateFormat="MMM d, yyyy"
          ref={startDatePickerRef}
          selectsStart={hasRecurrence}
          value={showStartPlaceholder ? startPlaceholder : undefined}
          customInput={
            <CalendarInput
              aria-label="Start date"
              w={inputWidth}
              onChange={handleStartChange}
            />
          }
          selected={
            showStartPlaceholder && fallbackStartDate
              ? fallbackStartDate
              : startDate
          }
          onChange={handleStartChange}
          onKeyDown={handleStartKeyDown}
        />
        <Text color="customgray.alsolight">to</Text>
        <DatePicker
          allowSameDay={showEndPlaceholder}
          dateFormat="MMM d, yyyy"
          endDate={hasRecurrence ? untilDate : adjustedEndDate}
          // An unset start is an open edge, not a bound: nothing to invert.
          minDate={showStartPlaceholder ? undefined : startDate}
          ref={endDatePickerRef}
          selectsEnd
          customInput={
            <CalendarInput
              aria-label={hasRecurrence ? 'Until date' : 'End date'}
              w={inputWidth}
              onBlur={() => endDatePickerRef.current?.setOpen(false)}
              onChange={hasRecurrence ? handleUntilChange : handleEndChange}
            />
          }
          selected={
            showEndPlaceholder && fallbackEndDate
              ? fallbackEndDate
              : selectedEndDate
          }
          startDate={
            !hasRecurrence || untilDate || isMouseOverCalendar
              ? startDate
              : undefined
          }
          value={
            showEndPlaceholder
              ? endPlaceholder
              : hasRecurrence && !untilDate
                ? 'Indefinitely'
                : undefined
          }
          onChange={hasRecurrence ? handleUntilChange : handleEndChange}
          onDayMouseEnter={() => setIsMouseOverCalendar(true)}
          onKeyDown={handleEndKeyDown}
          onMonthMouseLeave={() => setIsMouseOverCalendar(false)}
        >
          {hasRecurrence && (
            <Checkbox
              color="customgray.mid"
              isChecked={indefinitely}
              onChange={handleIndefinitelyChange}
            >
              Indefinitely
            </Checkbox>
          )}
        </DatePicker>
      </Flex>
    </Flex>
  );
};
